import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { OriginProvider, useChainedOrigin } from '@/lib/readiness/nav'
import { useArchiveQueue } from '@/components/readiness/EvidenceArchive'
import { ArchiveConfirmModal, type ArchiveConsequence } from '@/components/readiness/EvidenceArchiveUi'
import { isPlanLocked } from '@/lib/migration/usePlan'
import { BackLink, RdPage } from '@/components/readiness/shell'
import { useTheme } from '@/contexts/ThemeContext'
import { useReadiness } from '@/lib/readiness/useReadiness'
import { useReadinessStore, evidenceFor } from '@/stores/readinessStore'
import { findObjective, domainColor, levelName, tiersOf, tierForScore, sectorBenchmark } from '@/lib/indexes/registry'
import { liveLevel, liveScore, needsConfirmation, needsScoreConfirmation, tierScore } from '@/lib/readiness/score'
import { assertScale, fmtScore } from '@/lib/readiness/scale'
import { SIGNAL_RULES, QPI_SIGNAL_RULES } from '@/lib/readiness/signals'
import { LEVELS, levelNum, levelFromNum, transitionFor, type Evidence, type LevelCode } from '@/api/types/readiness'
import { partASectionsUsing } from '@/lib/migration/engine'
import { timeAgo } from '@/lib/readiness/util'
import { Chip, DomainDot, Icon, ScoreBar, SectionLabel } from '@/components/readiness/primitives'
import { ConfirmPanel, ScorePanel } from '@/components/readiness/ConfirmPanel'
import { PreliminaryNotice } from '@/components/readiness/PreliminaryNotice'
import { EvidenceModal, AssignModal, TargetLevelModal } from '@/components/readiness/modals'
import { Term } from '@/components/readiness/Term'
import { LevelTrack, type LevelStep } from '@/components/readiness/charts'
import { formatDate } from '@/components/common/format'
import { useReadinessModule } from '@/lib/readiness/module'
import { capsOf, monthEnd, objectiveDependencyAssets, objectiveHasEstateScope, plusDays, rollup, stepState } from '@/lib/catalogue/engine'
import { computeProjection } from '@/lib/readiness/projection'
import { stepKey } from '@/lib/readiness/scope'
import { followKey, useNotificationStore } from '@/stores/notificationStore'
import { StepDependency, GatedOptions, ExternalDependenciesCard, EarliestAchievableCard } from '@/components/readiness/DependencyParts'

const SOURCE_LABEL = { discovery: 'Auto · Discovery', playbook: 'Derived · Playbook', manual: 'Manual' } as const
const SOURCE_COLOR = { discovery: 'var(--rd-status-ok-text)', playbook: 'var(--rd-status-info)', manual: 'var(--rd-text-secondary)' } as const

export default function ObjectiveDetail() {
  const { code = '' } = useParams()
  const ctx = useReadiness()
  const { index, scope, orgId, indexId, actor } = ctx
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const found = useMemo(() => findObjective(index, code), [index, code])
  const scale = assertScale(index)

  const confirmLevel = useReadinessStore((s) => s.confirmLevel)
  const confirmScore = useReadinessStore((s) => s.confirmScore)
  const setDetails = useReadinessStore((s) => s.setDetails)
  const setBasis = useReadinessStore((s) => s.setBasis)
  const attachCsaReport = useReadinessStore((s) => s.attachCsaReport)
  const addEvidence = useReadinessStore((s) => s.addEvidence)
  const archiveEvidence = useReadinessStore((s) => s.archiveEvidence)
  const restoreEvidence = useReadinessStore((s) => s.restoreEvidence)
  const track = useReadinessStore((s) => s.track)
  // Iteration 5 §13: archive is deferred for the undo window; undo writes nothing.
  const queue = useArchiveQueue({
    onCommit: (id) => archiveEvidence(orgId, indexId, id, actor),
    onUndo: (id) => track(orgId, indexId, { name: 'readiness.evidence_archive_undone', evidence_id: id }),
  })
  const [confirmArchive, setConfirmArchive] = useState<{ item: Evidence; consequences: ArchiveConsequence[] } | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  // Iteration 5 §4: this page's origin for the links it renders (catalogue
  // entries open with `← {code}` and unwind back here).
  const chained = useChainedOrigin(code)
  const createAction = useReadinessStore((s) => s.createPlaybookAction)
  const setLinkStatus = useReadinessStore((s) => s.setLinkStatus)
  const fileRef = useRef<HTMLInputElement>(null)

  const [modal, setModal] = useState<null | 'evidence' | 'owner' | 'due' | 'target' | { assign: number }>(null)
  // Iteration 3 §12–§13: dependency-aware steps, follow, deep links to History.
  const m = useReadinessModule(ctx)
  const setStepChoice = useReadinessStore((s) => s.setStepChoice)
  const follows = useNotificationStore((s) => s.follows[followKey(orgId, indexId, actor)])
  const toggleFollow = useNotificationStore((s) => s.toggleFollow)
  const following = (follows ?? []).includes(code)
  const location = useLocation()
  const [showAllHistory, setShowAllHistory] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [arrived, setArrived] = useState<string | null>(null)
  const baseProjection = useMemo(() => (index.scale === 'levels' ? computeProjection(index, scope.events ?? []) : null), [index, scope.events])
  const arrivedEventId = location.hash.startsWith('#ev_') ? location.hash.slice(4) : null
  useEffect(() => {
    if (!arrivedEventId) return
    const all = scope.history.filter((h) => h.objective_code === code || h.kind === 'snapshot')
    const idx = all.findIndex((h) => h.event_id === arrivedEventId)
    if (idx >= 6) setShowAllHistory(true)
    setExpanded(idx >= 0 ? arrivedEventId : null)
    setArrived(arrivedEventId)
    const t = window.setTimeout(() => {
      const el = document.getElementById(idx >= 0 ? `ev_${arrivedEventId}` : 'rd-history')
      el?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' })
    }, 80)
    const clear = window.setTimeout(() => setArrived(null), 2400)
    return () => { window.clearTimeout(t); window.clearTimeout(clear) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedEventId, code])

  if (!found) return <Navigate to={`/readiness?index=${indexId}`} replace />
  const { domain, objective } = found
  const color = domainColor(domain, theme)
  const state = scope.states[code]
  const level = liveLevel(state)
  const score = liveScore(state)
  const n = levelNum(level)
  const next = level === 'L3' ? null : levelFromNum(n + 1)
  const transition = transitionFor(level)
  const bullets = scale === 'levels' ? (objective.recommendations[transition] ?? []) : (objective.actions ?? []).map((a) => a.text)
  const actionRefs = scale === 'levels' ? [] : (objective.actions ?? [])
  const evidence = evidenceFor(scope, code).filter((e) => !queue.isPending(e.id))
  const archivedEvidence = scope.evidence.filter((e) => e.objective_code === code && !!e.archived_at)
  const links = scope.links.filter((l) => l.objective_code === code && (scale === 'levels' ? l.transition === transition : true))
  const inProgress = links.filter((l) => l.status !== 'open').length
  const manualOnly = scale === 'levels' ? SIGNAL_RULES.filter((r) => r.objective_code === code).every((r) => r.signal_key === 'manual_only') : !QPI_SIGNAL_RULES.some((r) => r.objective_code === code)
  const historyAll = scope.history.filter((h) => h.objective_code === code || h.kind === 'snapshot')
  const history = showAllHistory ? historyAll : historyAll.slice(0, 6)
  // Dependency state per recommended step: computed, never stored (§12.3).
  const { usage, technologies, capabilities } = m.catalogue
  const steps3 = scale === 'levels' ? bullets.map((_, i) => stepState(code, transition, i, m.mscope, usage, technologies, capabilities)) : []
  const gatedSteps = steps3.filter((x) => x.state === 'vendor_gated')
  const firstGated = gatedSteps[0] ?? null
  const choiceOf = (i: number) => scope.step_choices?.[stepKey(code, transition, i)] ?? null
  const arrivedStep = arrived && scope.events.find((e) => e.id === arrived)?.event_type === 'dependency_changed' ? firstGated?.bullet_index ?? null : null
  // Earliest achievable (§12.2): next level with the gated step at the vendor
  // date, or at its expected dwell (alternative / waiver); L3 at current pace.
  const confirmedLvl = !state || state.status === 'unset' || state.status === 'suggested' ? 0 : levelNum(state.level)
  const reachBy = (p: typeof baseProjection, lvl: number) => {
    const o = p?.objectives.find((x) => x.code === code)
    if (!o) return null
    return lvl <= confirmedLvl ? new Date().toISOString() : o.path[lvl - confirmedLvl - 1] ?? null
  }
  const nextNum = next ? levelNum(next) : null
  const altDate = nextNum != null ? reachBy(baseProjection, nextNum) : null
  const vendorGate = firstGated?.vendor_date ? plusDays(monthEnd(firstGated.vendor_date), 30) : null
  const waitDate = altDate && vendorGate ? (vendorGate > altDate ? vendorGate : altDate) : vendorGate ?? altDate
  const l3Date = m.projection?.objectives.find((x) => x.code === code)?.l3_by ?? null
  const scopeAssets = objectiveDependencyAssets(code, m.mscope)
  const feeds = partASectionsUsing(code)
  // Level over time (§8.5): what each assessment recorded for this objective, then live.
  const steps: LevelStep[] = [
    ...scope.snapshots.map((s) => ({ key: s.id, label: s.name, date: s.created_at, level: s.items.find((i) => i.objective_code === code)?.level ?? ('L0' as LevelCode) })),
    { key: 'live', label: 'Live', date: new Date().toISOString(), level, live: true, unconfirmed: needsConfirmation(state) },
  ]
  const dataSources = scale === 'levels'
    ? Array.from(new Set(SIGNAL_RULES.filter((r) => r.objective_code === code && r.signal_key !== 'manual_only').map((r) => r.signal_key.split('.')[0]))).concat('manual')
    : Array.from(new Set(QPI_SIGNAL_RULES.filter((r) => r.objective_code === code).map((r) => r.signal_key.split('.')[0]))).concat('manual')
  const q = `?index=${indexId}`
  // Opened from the Review & save modal's "Confirm →": return to it after confirming (I2 §8.1).
  const returnToReview = params.get('return') === 'review'
  const afterConfirm = () => {
    if (returnToReview) navigate(`/readiness${q}&review=1`)
  }
  const tier = scale === 'score_0_10' ? tierForScore(index, code, score) : null

  // "Why this level" explanation (§8.3), generated from the signal rules.
  const why = (() => {
    if (!state || state.status === 'unset') return `No ${scale === 'levels' ? 'level' : 'score'} has been set yet. Add evidence and ${scale === 'levels' ? 'declare a level' : 'pick a tier'}, or run a sync.`
    if (scale === 'score_0_10') {
      if (state.status === 'suggested') return `Quantizant suggests ${fmtScore(state.suggested_score)} (tier ${state.suggested_tier}): ${state.suggested_reason ?? ''}`
      const t = tierForScore(index, code, state.score)
      return `Set by ${state.set_by} on ${formatDate(state.set_at)}${state.note ? `: ${state.note.replace(/[.\s]+$/, '')}.` : '.'}${t ? ` Matches tier ${t.n}: "${t.statement}" (${t.source_page}).` : ''}${needsScoreConfirmation(state) ? ` The latest sync suggests ${fmtScore(state.suggested_score)}: ${state.suggested_reason ?? ''}` : ''}`
    }
    if (state.status === 'suggested') return `Quantizant suggests ${state.suggested_level}: ${state.suggested_reason ?? ''} The ${state.suggested_level} descriptor reads: "${objective.levels[state.suggested_level ?? 'L0']}"`
    const base = `Set by ${state.set_by} on ${formatDate(state.set_at)}${state.note ? `: ${state.note.replace(/[.\s]+$/, '')}.` : '.'}`
    const sug = needsConfirmation(state) && state.suggested_level ? ` The latest sync suggests ${state.suggested_level}: ${state.suggested_reason ?? ''}` : state.suggested_reason ? ` Sync: ${state.suggested_reason}` : ''
    return `${base}${sug}`
  })()

  // Archive (I5 §13.2): confirm only when it has consequences; otherwise the
  // row collapses and the undo toast opens.
  const requestArchive = (e: (typeof evidence)[number]) => {
    const consequences: ArchiveConsequence[] = []
    const confirmedLevel = state && (state.status === 'confirmed' || state.status === 'declared') ? state.level : null
    if (confirmedLevel && confirmedLevel !== 'L0' && evidence.length === 1) consequences.push({ kind: 'last_for_level', level: confirmedLevel })
    const plan = m.plan.plan
    if (plan && !isPlanLocked(plan)) {
      const text = [plan.s1_leadership, plan.s3_crypto_management, plan.s4_risk_framework?.elaboration, plan.s5_procurement?.elaboration].filter(Boolean).join('\n')
      if (e.title && text.includes(e.title)) consequences.push({ kind: 'cited_in_plan', version: plan.plan_state.n })
    }
    if (consequences.length) setConfirmArchive({ item: e, consequences })
    else queue.archive(e)
  }

  const historyDot = (kind: string) =>
    kind === 'suggestion' ? 'var(--rd-status-warn)' : kind === 'playbook' ? 'var(--rd-status-info)' : kind === 'snapshot' ? 'var(--rd-status-neutral)' : kind === 'coverage' ? 'var(--rd-coverage)' : kind === 'dependency' ? 'var(--rd-projection)' : 'var(--rd-text-secondary)'

  return (
    <OriginProvider origin={chained}>
    <RdPage
      back={<BackLink label={returnToReview ? 'Back to Review & save' : undefined} onClick={returnToReview ? () => navigate(`/readiness${q}&review=1`) : undefined} />}
      title={objective.title}
      subtitle={
        <span className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wider" style={{ color }}>
            <DomainDot domain={domain} theme={theme} size={8} /> {domain.name}
          </span>
          <span className="rd-mono text-[12.5px]">{code}</span>
          {gatedSteps.length > 0 && <Chip tone="warn" style={{ fontWeight: 600 }}><span data-testid="gated-chip">{gatedSteps.length} step vendor-gated</span></Chip>}
          {objective.no_regrets && (
            <Term index={index} term="No-regrets" className="!no-underline"><Chip tone="chip">NO-REGRETS</Chip></Term>
          )}
        </span>
      }
      right={
        <>
          <button type="button" className="rd-btn" onClick={() => toggleFollow(orgId, indexId, actor, code)} aria-pressed={following} data-testid="follow-objective"><Icon name={following ? 'check' : 'plus'} size={14} /> {following ? 'Following' : 'Follow'}</button>
          <button type="button" className="rd-btn" onClick={() => setModal('due')}><Icon name="calendar" size={14} /> Set due date</button>
          <button type="button" className="rd-btn" onClick={() => setModal('owner')}><Icon name="user" size={14} /> Assign owner</button>
          <button type="button" className="rd-btn rd-btn-primary" onClick={() => setModal('evidence')} data-testid="add-evidence"><Icon name="plus" size={14} /> Add evidence</button>
        </>
      }
      testId="objective-detail"
    >
      <PreliminaryNotice index={index} />
      <p className="mb-5 max-w-3xl text-[14px] leading-relaxed" style={{ color: 'var(--rd-text-secondary)' }}>{objective.description}</p>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-5">
          {/* Card 1: level descriptors (§8.2) or tier statements (I2 §11) */}
          {scale === 'score_0_10' ? (
          <section className="rd-card p-5" aria-labelledby="rd-levels">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SectionLabel><span id="rd-levels">Score · tier statements</span></SectionLabel>
              {tier && state?.status !== 'unset' && <Chip tone="tile" style={{ fontWeight: 700, letterSpacing: '0.06em' }}>TIER {tier.n} OF {tiersOf(index, code).length}</Chip>}
            </div>
            <div className="mt-3 flex items-center gap-4">
              <span className="rd-mono text-[30px] font-bold" style={{ color: !state || state.status === 'unset' ? 'var(--rd-text-muted)' : color }}>{!state || state.status === 'unset' ? '—' : fmtScore(score)}</span>
              <div className="min-w-0 flex-1"><ScoreBar score={score} color={color} sector={sectorBenchmark(index, code)} suggested={state?.status === 'suggested'} /></div>
            </div>
            <ol className="mt-3 space-y-2">
              {tiersOf(index, code).map((t) => {
                const cur = tier?.n === t.n && state && state.status !== 'unset'
                return (
                  <li key={t.n} className="rd-tile p-3 text-[12.5px] leading-relaxed" style={cur ? { border: `2px solid ${color}`, background: `${color}14` } : { border: '2px solid transparent', color: 'var(--rd-text-muted)' }}>
                    <span className="rd-mono font-semibold" style={{ color: 'var(--rd-text-secondary)' }}>Tier {t.n} · {tierScore(t.n, tiersOf(index, code).length).toFixed(1)}</span> {t.statement}
                    <span className="block text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>{t.source_page}</span>
                  </li>
                )
              })}
            </ol>
            <p className="mt-3 text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}><b style={{ color: 'var(--rd-text-muted)' }}>Rationale.</b> {objective.rationale}</p>
          </section>
          ) : (
          <section className="rd-card p-5" aria-labelledby="rd-levels">
            <SectionLabel><span id="rd-levels">Readiness level</span></SectionLabel>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>
              Level descriptors from {index.name} {index.version}, Annex A
              {index.transcription.status !== 'verified' && ' · descriptor text pending verification against the published Annex A'}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {LEVELS.map((l) => {
                const i = levelNum(l)
                const isCur = l === level
                const isNext = next === l
                return (
                  <div key={l} className="rd-tile p-3" style={{ opacity: i < n ? 0.7 : 1, border: isCur ? `2px solid ${color}` : '2px solid transparent', background: isCur ? `${color}14` : undefined }}>
                    <div className="flex items-center justify-between">
                      <p className="text-[14px] font-bold"><span className="rd-mono">{l}</span> · {levelName(index, l)}</p>
                      {isCur && <span className="rd-chip" style={{ background: color, color: 'var(--accent-ink)', fontWeight: 700 }}>CURRENT</span>}
                      {isNext && <span className="text-[11px] font-bold" style={{ color: 'var(--rd-accent-link)' }}>NEXT</span>}
                    </div>
                    <div className="my-2" style={{ height: 6, borderRadius: 3, background: i <= n ? color : 'var(--rd-border-control)' }} />
                    <p className="text-[12.5px] leading-relaxed" style={{ color: isCur ? 'var(--rd-text-secondary)' : 'var(--rd-text-muted)' }}>{objective.levels[l]}</p>
                  </div>
                )
              })}
            </div>
            <p className="mt-3 text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}><b style={{ color: 'var(--rd-text-muted)' }}>Rationale.</b> {objective.rationale}</p>
          </section>
          )}

          {/* Card 2: evidence (§8.3) */}
          <section className="rd-card p-5" aria-labelledby="rd-evidence">
            <SectionLabel><span id="rd-evidence">Why {scale === 'levels' ? level : !state || state.status === 'unset' ? 'not scored' : fmtScore(score)} · <Term index={index} term="Evidence" className="!no-underline uppercase">evidence</Term></span></SectionLabel>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>{evidence.length} {evidence.length === 1 ? 'item' : 'items'}{ctx.lastSuccess ? ` · last synced ${timeAgo(ctx.lastSuccess.finished_at ?? ctx.lastSuccess.started_at)}` : ''}</p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-[13.5px]" style={{ minWidth: 520 }}>
                <thead>
                  <tr className="text-left" style={{ color: 'var(--rd-text-muted)', borderBottom: '1px solid var(--rd-border-subtle)' }}>
                    <th className="py-2 pr-3 font-medium" style={{ width: 170 }}>Source</th>
                    <th className="py-2 pr-3 font-medium">Evidence</th>
                    <th className="py-2 pr-3 font-medium" style={{ width: 120 }}>Date</th>
                    <th className="py-2 font-medium" style={{ width: 150 }}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {evidence.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center" style={{ color: 'var(--rd-text-muted)' }}>No evidence yet. Run a sync or add evidence.</td></tr>
                  )}
                  {evidence.map((e) => {
                    const sourceText = e.source === 'manual' ? `Manual · ${e.added_by}` : SOURCE_LABEL[e.source]
                    return (
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--rd-border-subtle)' }} data-testid={`evidence-row-${e.id}`}>
                      {/* Source is one line (I5 §14a): ellipsis, full text in title. */}
                      <td className="py-2 pr-3" style={{ color: SOURCE_COLOR[e.source], maxWidth: 170 }}>
                        <span className="flex items-center gap-1.5 whitespace-nowrap" title={sourceText}>
                          <Icon name={e.source === 'manual' ? 'file' : e.source === 'playbook' ? 'link' : 'check'} size={13} className="shrink-0" />
                          <span className="min-w-0 truncate" data-testid="evidence-source">{sourceText}</span>
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <p className="font-medium">{e.title}</p>
                        {e.summary && <p className="text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{e.summary}</p>}
                      </td>
                      <td className="rd-mono rd-id py-2 pr-3 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}>{formatDate(e.occurred_at)}</td>
                      <td className="py-2 text-[12.5px]">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          {e.link && (e.link.startsWith('http') ? <a href={e.link} target="_blank" rel="noreferrer" className="rd-link">Open file →</a> : <Link to={e.link} className="rd-link">{e.link.startsWith('/inventories') ? 'Inventory →' : e.link.startsWith('/readiness') ? 'Plan →' : 'Open →'}</Link>)}
                          <button type="button" className="rd-link" style={{ color: 'var(--rd-text-muted)' }} onClick={() => requestArchive(e)} data-testid={`archive-${e.id}`}>Archive</button>
                        </span>
                      </td>
                    </tr>
                    )
                  })}
                  {showArchived && archivedEvidence.map((e) => (
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--rd-border-subtle)', opacity: 0.55 }} data-testid={`evidence-archived-${e.id}`}>
                      <td className="py-2 pr-3" style={{ maxWidth: 170 }}><span className="block truncate whitespace-nowrap" title={e.source === 'manual' ? `Manual · ${e.added_by}` : SOURCE_LABEL[e.source]}>{e.source === 'manual' ? `Manual · ${e.added_by}` : SOURCE_LABEL[e.source]}</span></td>
                      <td className="py-2 pr-3"><p className="font-medium line-through">{e.title}</p><p className="text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Archived {formatDate(e.archived_at!)}</p></td>
                      <td className="rd-mono rd-id py-2 pr-3 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}>{formatDate(e.occurred_at)}</td>
                      <td className="py-2 text-[12.5px]"><button type="button" className="rd-link" onClick={() => restoreEvidence(orgId, indexId, e.id, actor)} data-testid={`restore-${e.id}`}>Restore</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[12.5px] leading-relaxed" style={{ color: 'var(--rd-text-faint)' }}>{why}</p>
            {archivedEvidence.length > 0 && (
              <p className="mt-2 text-[12.5px]"><button type="button" className="rd-link" onClick={() => setShowArchived((v) => !v)} aria-expanded={showArchived} data-testid="show-archived">{showArchived ? 'Hide archived' : `Show archived (${archivedEvidence.length})`}</button></p>
            )}
          </section>

          {/* Card 3: recommended steps (§8.4) */}
          <section className="rd-card p-5" aria-labelledby="rd-steps">
            <SectionLabel><span id="rd-steps">{scale === 'levels' ? (next ? `To reach ${next} · recommended steps` : 'Sustain') : 'Next · recommended actions'}</span></SectionLabel>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>{scale === 'levels' ? `From ${index.name} ${next ? `${level}→${next}` : 'L3 sustain'}` : `Mapped by Quantizant from ${index.report?.short ?? index.name}`} · {inProgress} of {bullets.length} in progress</p>
            {firstGated && next && (
              <div className="mt-3 flex items-start gap-2 rounded-[10px] px-3 py-2.5 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-text-secondary)' }} role="status" data-testid="gated-banner">
                <Icon name="warn" size={14} style={{ color: 'var(--rd-status-warn)', marginTop: 2 }} />
                <span>Step {firstGated.bullet_index + 1} depends on an external technology that is not yet PQC. At current vendor dates the earliest {next} is <b>{waitDate ? new Date(waitDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—'}</b>; with an alternative or a waiver for that step, <b>{altDate ? new Date(altDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—'}</b>.</span>
              </div>
            )}
            <ul className="mt-3 space-y-2">
              {bullets.map((b, i) => {
                const link = links.find((l) => l.bullet_index === i)
                const status = link?.status ?? 'open'
                return (
                  <li key={i} className={`rd-tile flex flex-wrap items-start gap-3 p-3 ${arrivedStep === i ? 'rd-arrived' : ''}`} data-testid={`step-${i}`}>
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full" style={{ border: `2px solid ${status === 'done' ? 'var(--rd-status-ok)' : status === 'in_progress' ? 'var(--rd-status-info)' : 'var(--rd-border-control)'}`, color: 'var(--rd-status-ok-text)' }} aria-label={status.replace('_', ' ')}>
                      {status === 'done' && <Icon name="check" size={12} />}
                      {status === 'in_progress' && <span className="h-2 w-2 rounded-full" style={{ background: 'var(--rd-status-info)' }} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] leading-snug">{b}{actionRefs[i] ? <span className="text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}> · {actionRefs[i].kind === 'metric' ? 'Monitoring metric' : actionRefs[i].kind === 'recommendation' ? 'Strategic recommendation' : `Stage action · ${actionRefs[i].stage ?? ''}`} · {actionRefs[i].source_page}</span> : null}</p>
                      {actionRefs[i]?.start_today && <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}><b>Start today:</b> {actionRefs[i].start_today}</p>}
                      {actionRefs[i]?.pitfall && <p className="mt-0.5 text-[12.5px]" style={{ color: 'var(--rd-status-warn)' }}><b>Pitfall to avoid:</b> {actionRefs[i].pitfall}</p>}
                      <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
                        {link ? (
                          <>Linked Playbook action · {link.action_title}{link.owner ? ` · Owner: ${link.owner}` : ''}{link.due_at ? ` · Due ${formatDate(link.due_at)}` : ''}</>
                        ) : (
                          <>Not yet linked · <button type="button" className="rd-link" onClick={() => createAction(orgId, indexId, { objective_code: code, transition, bullet_index: i, title: b, owner: null, due_at: null }, actor)}>Create Playbook action</button></>
                        )}
                        {code === 'RA-1' && i === 0 && <span> · Feeds RA-2</span>}
                        {choiceOf(i)?.option === 'waiver' && <> · <Chip tone="tile">deferred, justified</Chip></>}
                      </p>
                      {steps3[i] && <StepDependency st={steps3[i]} facts={m.mscope?.facts ?? null} q={q} />}
                      {steps3[i]?.state === 'vendor_gated' && (() => {
                        const g = steps3[i].gated[0]
                        const alts = technologies.filter((t) => t.id !== g.technology.id && t.category === g.technology.category)
                        return (
                          <GatedOptions
                            st={steps3[i]}
                            choice={choiceOf(i)}
                            category={g.technology.category}
                            alternatives={{ ready: alts.filter((t) => rollup(capsOf(t.id, capabilities)) === 'pqc_ready').length, total: alts.length + 1 }}
                            onChoose={(o) => setStepChoice(orgId, indexId, code, transition, i, { option: o }, actor)}
                            onCompare={() => { setStepChoice(orgId, indexId, code, transition, i, { option: 'pilot' }, actor); navigate(`/readiness/catalogue/${g.technology.id}${q}`) }}
                            onWaiver={() => {
                              // The waiver is recorded on the step itself: the step then reads
                              // "deferred, justified" until the vendor date. The platform Waivers
                              // register is not part of this portal.
                              const expiry = steps3[i].vendor_date ? monthEnd(steps3[i].vendor_date!).slice(0, 10) : ''
                              setStepChoice(orgId, indexId, code, transition, i, { option: 'waiver', waiver_expiry: expiry || null, waiver_ref: g.item.code }, actor)
                            }}
                            onPlaybook={() => createAction(orgId, indexId, { objective_code: code, transition, bullet_index: i, title: b, owner: null, due_at: null }, actor)}
                          />
                        )
                      })()}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {link ? (
                        <select aria-label="Action status" className="rd-chip cursor-pointer appearance-none border-0" style={{ background: status === 'done' ? 'var(--rd-status-ok-bg)' : 'var(--rd-accent-chip-bg)', color: status === 'done' ? 'var(--rd-status-ok-text)' : 'var(--rd-status-info)', fontWeight: 600 }} value={status} onChange={(e) => setLinkStatus(orgId, indexId, link.id, e.target.value as typeof status, actor)}>
                          <option value="open">OPEN</option>
                          <option value="in_progress">IN PROGRESS</option>
                          <option value="done">DONE</option>
                        </select>
                      ) : (
                        <button type="button" className="rd-btn rd-btn-sm" onClick={() => setModal({ assign: i })}>Assign</button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
            {index.handbook?.url && (
              <a href={index.handbook.url} target="_blank" rel="noreferrer" className="rd-link mt-3 inline-flex items-center gap-1 text-[12.5px]">{index.handbook.name} <Icon name="external" size={12} /></a>
            )}
          </section>
        </div>

        {/* Right rail (§8.5) */}
        <div className="space-y-5">
          {scale === 'levels' ? (
            <ConfirmPanel index={index} state={state} evidenceCount={evidence.length} color={color} onConfirm={(l: LevelCode, note: string, cause?: 'coverage' | 'posture') => { confirmLevel(orgId, indexId, code, l, note, actor, cause); afterConfirm() }} />
          ) : (
            <ScorePanel index={index} code={code} state={state} evidenceCount={evidence.length} color={color} onConfirm={(input, note) => { confirmScore(orgId, indexId, code, input, note, actor); afterConfirm() }} />
          )}

          <section className="rd-card p-5" aria-labelledby="rd-details">
            <SectionLabel><span id="rd-details">Details</span></SectionLabel>
            <dl className="mt-2 space-y-2 text-[14px]">
              <Row label="Owner"><button type="button" className="rd-link" onClick={() => setModal('owner')}>{state?.owner ?? 'Assign'}</button></Row>
              <Row label={scale === 'levels' ? 'Target level' : 'Target score'}><button type="button" className="rd-link" onClick={() => setModal('target')}>{state?.target_level_text ?? 'Set'}</button></Row>
              <Row label="Due date"><button type="button" className="rd-link" onClick={() => setModal('due')}>{state?.due_at ? formatDate(state.due_at) : 'Set'}</button></Row>
              {scale === 'levels' && (
                <Row label="Basis of level">
                  <span className="flex flex-col items-end gap-0.5" data-testid="basis-of-level">
                    <span>{state?.basis === 'csa_report' ? 'CSA QRI report attached' : 'Internal judgement'}</span>
                    {state?.basis === 'csa_report' ? (
                      <button type="button" className="rd-link text-[11.5px]" onClick={() => setBasis(orgId, indexId, code, 'internal', actor)}>Revert to internal judgement</button>
                    ) : (
                      <>
                        <button type="button" className="rd-link text-[11.5px]" onClick={() => fileRef.current?.click()}><Icon name="upload" size={14} /> Attach CSA QRI report</button>
                        <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="sr-only" aria-label="Attach CSA QRI report" onChange={(e) => { const f = e.target.files?.[0]; if (f) attachCsaReport(orgId, indexId, code, f.name, actor); e.target.value = '' }} />
                      </>
                    )}
                  </span>
                </Row>
              )}
              <Row label="Data sources"><span className="capitalize">{dataSources.join(' · ')}</span></Row>
              <Row label="Feeds submission plan">{feeds.length ? feeds.map((f) => `Part A ${f}`).join(', ') : '—'}</Row>
            </dl>
          </section>

          {scale === 'levels' && (objectiveHasEstateScope(code) || steps3.some((x) => x.dependencies.length)) && (
            <ExternalDependenciesCard code={code} assetIds={scopeAssets.length ? scopeAssets : steps3.flatMap((x) => x.dependencies.map((d) => d.asset?.id ?? '')).filter(Boolean)} usage={usage} technologies={technologies} capabilities={capabilities} q={q} />
          )}
          {scale === 'levels' && <EarliestAchievableCard next={next} gatedStep={firstGated ? firstGated.bullet_index + 1 : null} waiting={waitDate} alternative={altDate} l3={l3Date} />}

          <section className="rd-card p-5" aria-labelledby="rd-history" id="rd-history">
            <SectionLabel><span id="rd-history">History</span></SectionLabel>
            {scope.snapshots.length > 0 && (
              <div className="mt-2" data-testid="level-track">
                <p className="text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>{scale === 'levels' ? 'Level by assessment → live' : 'Score by assessment → live'}</p>
                {scale === 'levels' && <LevelTrack index={index} steps={steps} />}
                {scale === 'score_0_10' && (
                  <p className="rd-mono mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}>{scope.snapshots.map((s) => fmtScore(s.items.find((i) => i.objective_code === code)?.score ?? null)).concat(fmtScore(score)).join(' → ')}</p>
                )}
              </div>
            )}
            <ol className="mt-3 space-y-3">
              {history.length === 0 && <li className="text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>No events yet.</li>}
              {history.map((h) => {
                const ev = h.event_id ? scope.events.find((e) => e.id === h.event_id) : null
                const open = expanded === h.event_id && !!h.event_id
                return (
                  <li key={h.id} id={h.event_id ? `ev_${h.event_id}` : undefined} className={`flex gap-2.5 p-1 text-[12.5px] ${arrived && arrived === h.event_id ? 'rd-arrived' : ''}`} data-testid={h.event_id ? `history-ev-${h.event_id}` : undefined}>
                    <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: historyDot(h.kind) }} aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      {ev && (ev.dependency || ev.event_type === 'scope_expanded') ? (
                        <button type="button" className="block text-left hover:underline" onClick={() => setExpanded(open ? null : h.event_id!)} aria-expanded={open}>{h.text}</button>
                      ) : (
                        <span className="block">{h.text}</span>
                      )}
                      <span style={{ color: 'var(--rd-text-faint)' }}>{h.actor} · {formatDate(h.at)}{h.detail ? ` · ${h.detail}` : ''}</span>
                      {open && ev?.dependency && (
                        <span className="mt-1 block rounded-[8px] p-2" style={{ background: 'var(--rd-bg-tile)', color: 'var(--rd-text-secondary)' }} data-testid="history-expanded">
                          {ev.dependency.technology}{ev.dependency.capability ? ` · ${ev.dependency.capability.replace(/_/g, ' ')}` : ''}{ev.dependency.old || ev.dependency.new ? ` · ${ev.dependency.old ?? '—'} → ${ev.dependency.new ?? '—'}` : ''}{ev.dependency.asset ? ` · ${ev.dependency.asset}` : ''} · <Link to={`/readiness/catalogue/${ev.dependency.technology_id}${q}`} className="rd-link">Catalogue ↗</Link>
                        </span>
                      )}
                      {open && ev?.event_type === 'scope_expanded' && (
                        <span className="mt-1 block rounded-[8px] p-2" style={{ background: 'var(--rd-coverage-bg)', color: 'var(--rd-coverage)' }}>More assets were found, so the level now describes a larger estate. This is better visibility, not regression.</span>
                      )}
                    </span>
                  </li>
                )
              })}
            </ol>
            {historyAll.length > 6 && <button type="button" className="rd-link mt-2 block text-[12.5px]" onClick={() => setShowAllHistory((v) => !v)}>{showAllHistory ? 'Show recent' : `Show all ${historyAll.length}`}</button>}
            <Link to={`/readiness/progress${q}`} className="rd-link mt-3 inline-block text-[12.5px]">All assessments →</Link>
          </section>
        </div>
      </div>

      {queue.toasts}
      {confirmArchive && (
        <ArchiveConfirmModal
          title={confirmArchive.item.title}
          consequences={confirmArchive.consequences}
          onCancel={() => setConfirmArchive(null)}
          onConfirm={() => { queue.archive(confirmArchive.item); setConfirmArchive(null) }}
        />
      )}
      {modal === 'evidence' && (
        <EvidenceModal
          index={index}
          objectiveCode={code}
          manualOnly={manualOnly}
          onClose={() => setModal(null)}
          onAdd={(input) => {
            addEvidence(orgId, indexId, { objective_code: code, ...input }, actor)
            setModal(null)
          }}
        />
      )}
      {modal === 'owner' && <AssignModal title="Assign owner" initialOwner={state?.owner} initialDue={state?.due_at} onClose={() => setModal(null)} onSave={(owner, due) => { setDetails(orgId, indexId, code, { owner, due_at: due }, actor); setModal(null) }} />}
      {modal === 'due' && <AssignModal title="Set due date" initialOwner={state?.owner} initialDue={state?.due_at} onClose={() => setModal(null)} onSave={(owner, due) => { setDetails(orgId, indexId, code, { owner, due_at: due }, actor); setModal(null) }} />}
      {modal === 'target' && <TargetLevelModal initial={state?.target_level_text ?? null} onClose={() => setModal(null)} onSave={(v) => { setDetails(orgId, indexId, code, { target_level_text: v }, actor); setModal(null) }} />}
      {modal && typeof modal === 'object' && (
        <AssignModal
          title="Assign recommendation"
          confirmLabel="Create Playbook action"
          onClose={() => setModal(null)}
          onSave={(owner, due) => {
            createAction(orgId, indexId, { objective_code: code, transition, bullet_index: modal.assign, title: bullets[modal.assign], owner, due_at: due }, actor)
            setModal(null)
          }}
        />
      )}
    </RdPage>
    </OriginProvider>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt style={{ color: 'var(--rd-text-muted)' }}>{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  )
}
